import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtUSD, fmtPct, API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { ChevronLeft, ChevronRight, X, Sparkles, Flame, Filter, Move, MessageSquare, BookOpen, NotebookPen, Video } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import VideoPlayer from "@/components/VideoPlayer";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const ChartTip = ({ active, payload, label }) => active && payload?.length ? (
  <div className="bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-2 text-xs font-mono-num">
    <div className="text-[color:var(--fg-3)]">{label}</div>
    <div className="text-[color:var(--fg)]">${payload[0].value.toFixed(2)}</div>
  </div>
) : null;

export default function Calendar() {
  const today = new Date();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shifting, setShifting] = useState(false);
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);

  const openDay = (dateStr) => {
    setSelectedTradeIds([]);
    setSelectedDay(dateStr);
  };
  const closeDay = () => {
    setSelectedTradeIds([]);
    setSelectedDay(null);
  };

  const shiftTradesTo = async (fromDate) => {
    const useSelection = selectedTradeIds.length > 0;
    const promptMsg = useSelection
      ? `Move ${selectedTradeIds.length} SELECTED trade(s) from ${fromDate} to which date? (YYYY-MM-DD)`
      : `Move ALL ${fromDate} trades to which date? (YYYY-MM-DD)`;
    const to = window.prompt(promptMsg, fromDate);
    if (!to || to === fromDate) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) { toast.error("Use format YYYY-MM-DD"); return; }
    setShifting(true);
    try {
      const body = useSelection
        ? { to_date: to, trade_ids: selectedTradeIds }
        : { from_date: fromDate, to_date: to };
      const r = await api.post("/trades/shift-date", body);
      toast.success(`MOVED ${r.data.moved} TRADE${r.data.moved === 1 ? "" : "S"} · ${fromDate} → ${to}`);
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["calendar-day"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      setSelectedDay(null);
      setSelectedTradeIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally { setShifting(false); }
  };

  const toggleTradeId = (id) => {
    setSelectedTradeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = (allIds) => {
    setSelectedTradeIds((prev) => (prev.length === allIds.length ? [] : allIds));
  };

  const coachThisDay = (date, dayInfo) => {
    const summary = dayInfo
      ? `pnl=$${dayInfo.total_pnl?.toFixed(2)} trades=${dayInfo.trades} winrate=${dayInfo.win_rate}%`
      : "";
    const q = `Coach me on ${date}. Look at every trade I took that day (${summary}). What went right, what went wrong, and what specific behaviors should I repeat tomorrow vs avoid? Be brutally honest.`;
    navigate(`/coach?prompt=${encodeURIComponent(q)}`);
  };

  const { data: days = [] } = useQuery({
    queryKey: ["calendar", year, month],
    queryFn: async () => (await api.get(`/calendar?year=${year}&month=${month}`)).data,
  });

  const { data: day } = useQuery({
    queryKey: ["calendar-day", selectedDay],
    queryFn: async () => (await api.get(`/calendar/${selectedDay}`)).data,
    enabled: !!selectedDay,
  });

  // Video clip counts per date — for showing a camera icon on the calendar cell.
  const { data: clipDates = {} } = useQuery({
    queryKey: ["video-clips", "dates"],
    queryFn: async () => (await api.get("/video/clips/dates")).data,
  });

  // Load clips for the selected day when the drawer opens.
  const { data: dayClips = [] } = useQuery({
    queryKey: ["video-clips", "day", selectedDay],
    queryFn: async () => (await api.get("/video/clips", { params: { date: selectedDay } })).data,
    enabled: !!selectedDay,
  });

  const dayMap = new Map(days.map((d) => [d.date, d]));
  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const monthTotal = days.reduce((s, d) => s + d.pnl, 0);
  const winningDays = days.filter((d) => d.pnl > 0).length;
  const losingDays = days.filter((d) => d.pnl < 0).length;

  const greenStreak = useMemo(() => {
    const sorted = [...days].sort((a, b) => (a.date < b.date ? 1 : -1));
    let streak = 0;
    for (const d of sorted) {
      if (d.pnl > 0) streak += 1;
      else break;
    }
    return streak;
  }, [days]);

  const filterTradesToDay = (dateStr) => {
    setSelectedDay(null);
    navigate(`/trades?date=${dateStr}`);
  };

  return (
    <div>
      <PageHeader eyebrow="CALENDAR" title="Daily P&L View" action={
        <div className="flex items-center gap-2 relative">
          {greenStreak > 0 && (
            <div className="inline-flex items-center gap-1.5 px-3 py-2 border border-[rgba(34,197,94,0.40)] bg-[rgba(34,197,94,0.10)] text-[color:var(--green)] text-xs font-mono-num mr-2" data-testid="green-streak">
              <Flame className="h-3.5 w-3.5" strokeWidth={1.5} /> {greenStreak} GREEN-DAY STREAK
            </div>
          )}
          <button onClick={prev} data-testid="cal-prev" className="p-2 bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)]"><ChevronLeft className="h-4 w-4" strokeWidth={1.5} /></button>
          <button onClick={() => setPickerOpen((s) => !s)} data-testid="cal-title-btn"
            className="px-4 py-2 font-mono-num text-sm border border-[color:var(--border)] hover:border-[color:var(--accent)] transition-colors min-w-[180px] text-center">
            {MONTHS[month - 1]} {year}
          </button>
          {pickerOpen && (
            <div className="absolute top-full right-0 mt-2 z-30 bg-[color:var(--bg)] border border-[color:var(--border)] p-4 w-[280px]" data-testid="cal-picker">
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setYear(year - 1)} className="text-[color:var(--fg-3)] hover:text-[color:var(--accent)]" data-testid="picker-prev-year"><ChevronLeft className="h-4 w-4" strokeWidth={1.5} /></button>
                <span className="font-mono-num text-sm">{year}</span>
                <button onClick={() => setYear(year + 1)} className="text-[color:var(--fg-3)] hover:text-[color:var(--accent)]" data-testid="picker-next-year"><ChevronRight className="h-4 w-4" strokeWidth={1.5} /></button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((m, i) => (
                  <button key={m} onClick={() => { setMonth(i + 1); setPickerOpen(false); }}
                    data-testid={`picker-month-${i + 1}`}
                    className={`px-2 py-1.5 text-xs font-mono-num transition-colors ${month === i + 1 ? "bg-[color:var(--accent)] text-black" : "bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] text-[color:var(--fg-2)]"}`}>
                    {m.slice(0, 3).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button onClick={next} data-testid="cal-next" className="p-2 bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)]"><ChevronRight className="h-4 w-4" strokeWidth={1.5} /></button>
        </div>
      } />

      <div className="p-8">
        {/* Month summary */}
        <div className="grid grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0 mb-6">
          <div className="p-4"><div className="label-eyebrow">MONTH P&L</div><div className={`font-mono-num text-2xl mt-1 ${monthTotal >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`} data-testid="cal-month-pnl">{fmtUSD(monthTotal)}</div></div>
          <div className="p-4"><div className="label-eyebrow">TRADING DAYS</div><div className="font-mono-num text-2xl mt-1 text-[color:var(--fg)]">{days.length}</div></div>
          <div className="p-4"><div className="label-eyebrow">GREEN DAYS</div><div className="font-mono-num text-2xl mt-1 text-[color:var(--green)]">{winningDays}</div></div>
          <div className="p-4"><div className="label-eyebrow">RED DAYS</div><div className="font-mono-num text-2xl mt-1 text-rose-400">{losingDays}</div></div>
        </div>

        {/* Calendar grid — 7 day columns + weekly summary column */}
        <div className="grid grid-cols-8 gap-0 border border-[color:var(--border)]">
          {DOW.map((d) => (
            <div key={d} className="p-3 label-eyebrow border-b border-r border-[color:var(--border)] text-center">{d}</div>
          ))}
          <div className="p-3 label-eyebrow border-b border-[color:var(--border)] text-center bg-[color:var(--surface)]/40">WEEK</div>
          {Array.from({ length: cells.length / 7 }, (_, weekIdx) => {
            const weekCells = cells.slice(weekIdx * 7, weekIdx * 7 + 7);
            let weekPnl = 0;
            let weekTrades = 0;
            let weekDays = 0;
            weekCells.forEach((d) => {
              if (!d) return;
              const dateStr = `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
              const info = dayMap.get(dateStr);
              if (info) {
                weekPnl += info.pnl;
                weekTrades += info.trades || 0;
                weekDays += 1;
              }
            });
            return (
              <React.Fragment key={`week-${weekIdx}`}>
                {weekCells.map((d, i) => {
                  const dateStr = d ? `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}` : null;
                  const info = dateStr ? dayMap.get(dateStr) : null;
                  const bg = info?.pnl > 0 ? "bg-emerald-500/15 hover:bg-emerald-500/25" : info?.pnl < 0 ? "bg-rose-500/15 hover:bg-rose-500/25" : "hover:bg-[color:var(--surface)]/60";
                  const isToday = dateStr === new Date().toISOString().slice(0, 10);
                  const grade = info?.grade || "";
                  const gradeColor = grade === "A" ? "bg-emerald-500/90 text-black"
                    : grade === "B" ? "bg-[color:var(--accent)]/30 text-[color:var(--accent)] border border-[color:var(--accent)]/60"
                    : grade === "C" ? "bg-amber-400/30 text-amber-200 border border-amber-400/60"
                    : grade === "D" ? "bg-orange-400/30 text-orange-200 border border-orange-400/60"
                    : grade === "F" ? "bg-rose-500/90 text-white"
                    : "";
                  return (
                    <button key={dateStr || `pad-${year}-${month}-${weekIdx}-${i}`} disabled={!d || !info}
                      onClick={() => info && openDay(dateStr)}
                      data-testid={d ? `cal-day-${d}` : undefined}
                      className={`relative min-h-[88px] p-2 border-r border-b border-[color:var(--border)] text-left transition-colors ${d ? bg : "bg-[color:var(--bg)]/50"} ${!info ? "cursor-default" : "cursor-pointer"} ${isToday ? "ring-1 ring-inset ring-[color:var(--accent)]/60" : ""}`}>
                      {d && (
                        <>
                          <div className={`text-xs font-mono-num ${info ? "text-[color:var(--fg)]" : "text-[color:var(--fg-3)]"}`}>{d}</div>
                          {info && (
                            <div className="mt-2 space-y-0.5">
                              <div className={`font-mono-num text-sm font-medium ${info.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>
                                {info.pnl >= 0 ? "+" : ""}{fmtUSD(info.pnl)}
                              </div>
                              <div className="text-[10px] text-[color:var(--fg-3)] font-mono-num">{info.trades}T · {fmtPct(info.win_rate)}</div>
                            </div>
                          )}
                          {grade && (
                            <div
                              className={`absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center font-mono-num text-[11px] font-bold ${gradeColor}`}
                              data-testid={`cal-grade-${dateStr}`}
                              title={`Self-grade: ${grade}`}
                            >
                              {grade}
                            </div>
                          )}
                          {clipDates[dateStr] > 0 && (
                            <div
                              className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-mono-num"
                              style={{ background: "rgba(168,85,247,0.2)", color: "var(--accent)", borderRadius: "4px" }}
                              title={`${clipDates[dateStr]} video clip${clipDates[dateStr] === 1 ? "" : "s"}`}
                              data-testid={`cal-clips-${dateStr}`}
                            >
                              <Video className="h-2.5 w-2.5" strokeWidth={2} /> {clipDates[dateStr]}
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
                <div
                  data-testid={`cal-week-${weekIdx}`}
                  className={`min-h-[88px] p-2 border-b border-[color:var(--border)] bg-[color:var(--surface)]/40 flex flex-col justify-center items-center text-center ${weekDays === 0 ? "opacity-40" : ""}`}
                >
                  <div className="label-eyebrow text-[10px] text-[color:var(--fg-3)]">WEEK {weekIdx + 1}</div>
                  <div className={`font-mono-num text-sm font-semibold mt-1 ${weekPnl > 0 ? "text-[color:var(--green)]" : weekPnl < 0 ? "text-[color:var(--loss)]" : "text-[color:var(--fg-3)]"}`}>
                    {weekDays > 0 ? `${weekPnl >= 0 ? "+" : ""}${fmtUSD(weekPnl)}` : "—"}
                  </div>
                  {weekDays > 0 && (
                    <div className="text-[10px] text-[color:var(--fg-3)] font-mono-num mt-0.5">
                      {weekDays}D · {weekTrades}T
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Day detail drawer */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70" onClick={closeDay}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-[color:var(--bg)] border border-[color:var(--border)]" data-testid="day-detail">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border)]">
              <div>
                <div className="label-eyebrow">DAILY DEBRIEF</div>
                <div className="font-heading text-2xl mt-1">{selectedDay}</div>
              </div>
              <button onClick={closeDay} data-testid="day-close" className="text-[color:var(--fg-3)] hover:text-[color:var(--fg)]"><X className="h-5 w-5" strokeWidth={1.5} /></button>
            </div>

            {day && (
              <div className="p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/recap/${selectedDay}`)}
                    data-testid="open-recap-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono-num bg-sky-400/10 text-sky-300 border border-sky-400/30 px-3 py-1.5 hover:bg-sky-400/20 transition-colors"
                  >
                    <BookOpen className="h-3 w-3" strokeWidth={1.5} />
                    VIEW FULL RECAP
                  </button>
                  <button
                    onClick={() => navigate(`/journal?date=${selectedDay}`)}
                    data-testid="open-journal-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono-num bg-[color:var(--accent)]/10 text-[color:var(--accent)] border border-[color:var(--accent)]/30 px-3 py-1.5 hover:bg-[color:var(--accent)]/20 transition-colors"
                  >
                    <NotebookPen className="h-3 w-3" strokeWidth={1.5} />
                    OPEN JOURNAL
                  </button>
                  <button
                    onClick={() => coachThisDay(selectedDay, day)}
                    disabled={!day.trades}
                    data-testid="coach-day-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono-num bg-[color:var(--accent)]/10 text-[color:var(--accent)] border border-[color:var(--accent)]/30 px-3 py-1.5 hover:bg-[color:var(--accent)]/20 transition-colors disabled:opacity-40"
                  >
                    <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
                    COACH ME ON THIS DAY
                  </button>
                  <button
                    onClick={() => shiftTradesTo(selectedDay)}
                    disabled={shifting || !day.trades}
                    data-testid="shift-day-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono-num bg-amber-400/10 text-amber-300 border border-amber-400/30 px-3 py-1.5 hover:bg-amber-400/20 transition-colors disabled:opacity-40"
                  >
                    <Move className="h-3 w-3" strokeWidth={1.5} />
                    {shifting
                      ? "MOVING…"
                      : selectedTradeIds.length > 0
                        ? `MOVE ${selectedTradeIds.length} SELECTED TRADE${selectedTradeIds.length === 1 ? "" : "S"}`
                        : "MOVE ALL TRADES TO ANOTHER DAY"}
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
                  <div className="p-3"><div className="label-eyebrow">DAY P&L</div><div className={`font-mono-num text-lg mt-1 ${day.total_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(day.total_pnl)}</div></div>
                  <div className="p-3"><div className="label-eyebrow">TRADES</div><div className="font-mono-num text-lg mt-1">{day.trades}</div></div>
                  <div className="p-3"><div className="label-eyebrow">AVG WIN</div><div className="font-mono-num text-lg mt-1 text-[color:var(--green)]">{fmtUSD(day.avg_win)}</div></div>
                  <div className="p-3"><div className="label-eyebrow">AVG LOSS</div><div className="font-mono-num text-lg mt-1 text-rose-400">{fmtUSD(day.avg_loss)}</div></div>
                </div>

                {day.equity_curve.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">INTRADAY EQUITY</div>
                    <div className="h-48 border border-[color:var(--border)] p-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={day.equity_curve}>
                          <defs>
                            <linearGradient id="day-eq" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={day.total_pnl >= 0 ? "#34d399" : "#fb7185"} stopOpacity={0.4} />
                              <stop offset="100%" stopColor={day.total_pnl >= 0 ? "#34d399" : "#fb7185"} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="time" stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                          <YAxis stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                          <Tooltip content={<ChartTip />} cursor={{ stroke: "#38bdf8", strokeWidth: 1, strokeDasharray: "2 4" }} />
                          <ReferenceLine y={0} stroke="#3f3f46" strokeDasharray="3 3" />
                          <Area type="monotone" dataKey="equity" stroke={day.total_pnl >= 0 ? "#34d399" : "#fb7185"} strokeWidth={2} fill="url(#day-eq)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {day.day_summary && (
                  <div className="border border-sky-500/30 bg-sky-500/5 p-4">
                    <div className="label-eyebrow text-sky-400 flex items-center gap-1.5 mb-2">
                      <Sparkles className="h-3 w-3" strokeWidth={1.5} /> WHAT WORKED · WHAT DIDN&apos;T · FIX TOMORROW
                    </div>
                    <p className="text-sm text-[color:var(--fg)] leading-relaxed whitespace-pre-wrap" data-testid="day-summary">{day.day_summary}</p>
                  </div>
                )}

                {dayClips.length > 0 && (
                  <div className="p-4" style={{ background: "rgba(168,85,247,0.05)", border: "1px solid rgba(168,85,247,0.30)", borderRadius: "12px" }} data-testid="day-clips-section">
                    <div className="label-eyebrow mb-3 flex items-center gap-1.5" style={{ color: "var(--accent)" }}>
                      <Video className="h-3 w-3" strokeWidth={1.5} /> {dayClips.length} VIDEO {dayClips.length === 1 ? "CLIP" : "CLIPS"} FROM THIS DAY
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {dayClips.map((c) => (
                        <div key={c.id} className="text-xs" data-testid={`day-clip-${c.id}`}>
                          <VideoPlayer
                            clip={c}
                            streamUrl={`${API_BASE}/video/clips/${c.id}/stream?token=${encodeURIComponent(localStorage.getItem("edge_token") || "")}`}
                          />
                          <div className="mt-1.5 flex items-center gap-2 text-[11px] font-mono-num">
                            <span style={{ color: "var(--fg)" }}>{c.symbol}</span>
                            <span style={{ color: c.pnl >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(c.pnl)}</span>
                            <span style={{ color: "var(--fg-3)" }}>{new Date(c.entry_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {day.day_grade && (
                  <div className="border border-amber-500/30 bg-amber-500/5 p-4 flex items-center justify-between">
                    <div>
                      <div className="label-eyebrow text-amber-400 mb-1">SELF-GRADE</div>
                      <div className="text-xs text-[color:var(--fg-3)]">Your assessment of today&apos;s execution.</div>
                    </div>
                    <div className="font-mono-num text-4xl text-amber-300 font-semibold tabular-nums" data-testid="day-grade">{day.day_grade}</div>
                  </div>
                )}

                {day.journal && (day.journal.summary || day.journal.wins || day.journal.mistakes || day.journal.lessons || day.journal.plan_tomorrow) && (
                  <div className="border border-[color:var(--accent)]/30 bg-[rgba(168,85,247,0.05)] p-4" data-testid="day-journal">
                    <div className="label-eyebrow text-[color:var(--accent)] flex items-center gap-1.5 mb-3">
                      <NotebookPen className="h-3 w-3" strokeWidth={1.5} /> JOURNAL ENTRY
                      <button
                        onClick={() => navigate(`/journal?date=${selectedDay}`)}
                        className="ml-auto text-[10px] text-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
                      >
                        EDIT →
                      </button>
                    </div>
                    {day.journal.summary && (
                      <div className="mb-3">
                        <div className="label-eyebrow text-[color:var(--fg-3)] mb-1">SUMMARY</div>
                        <p className="text-sm text-[color:var(--fg)] leading-relaxed whitespace-pre-wrap" data-testid="journal-summary-display">{day.journal.summary}</p>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3 text-xs">
                      {day.journal.wins && (
                        <div>
                          <div className="label-eyebrow text-[color:var(--green)] mb-1">WINS</div>
                          <p className="text-[color:var(--fg-2)] leading-relaxed whitespace-pre-wrap">{day.journal.wins}</p>
                        </div>
                      )}
                      {day.journal.mistakes && (
                        <div>
                          <div className="label-eyebrow text-rose-400 mb-1">MISTAKES</div>
                          <p className="text-[color:var(--fg-2)] leading-relaxed whitespace-pre-wrap">{day.journal.mistakes}</p>
                        </div>
                      )}
                      {day.journal.lessons && (
                        <div>
                          <div className="label-eyebrow text-amber-400 mb-1">LESSONS</div>
                          <p className="text-[color:var(--fg-2)] leading-relaxed whitespace-pre-wrap">{day.journal.lessons}</p>
                        </div>
                      )}
                      {day.journal.plan_tomorrow && (
                        <div>
                          <div className="label-eyebrow text-sky-400 mb-1">PLAN — NEXT SESSION</div>
                          <p className="text-[color:var(--fg-2)] leading-relaxed whitespace-pre-wrap">{day.journal.plan_tomorrow}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {day.takeaway && (
                  <div className="border border-[color:var(--accent)]/30 bg-[rgba(168,85,247,0.05)] p-4">
                    <div className="label-eyebrow text-[color:var(--accent)] flex items-center gap-1.5 mb-2">
                      <Sparkles className="h-3 w-3" strokeWidth={1.5} /> AI EDGE BRIEF
                    </div>
                    <p className="text-sm text-[color:var(--fg)] font-mono-num leading-relaxed whitespace-pre-wrap" data-testid="day-takeaway">{day.takeaway}</p>
                  </div>
                )}

                {day.trade_list?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label-eyebrow">TRADES THIS DAY · CHECK ROWS TO MOVE SELECTIVELY</div>
                      <button
                        onClick={() => toggleSelectAll(day.trade_list.map((t) => t.id).filter(Boolean))}
                        data-testid="select-all-trades"
                        className="text-[10px] font-mono-num text-[color:var(--fg-2)] hover:text-[color:var(--accent)]"
                      >
                        {selectedTradeIds.length === day.trade_list.length ? "CLEAR ALL" : "SELECT ALL"}
                      </button>
                    </div>
                    <div className="border border-[color:var(--border)] max-h-56 overflow-y-auto">
                      <table className="w-full text-xs font-mono-num">
                        <thead className="sticky top-0 bg-[color:var(--bg)] border-b border-[color:var(--border)]">
                          <tr className="text-[color:var(--fg-3)] label-eyebrow">
                            <th className="text-left p-2 w-8"></th>
                            <th className="text-left p-2">TIME</th>
                            <th className="text-left p-2">SYMBOL</th>
                            <th className="text-right p-2">QTY</th>
                            <th className="text-right p-2">P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {day.trade_list.map((t, i) => (
                            <tr
                              key={t.id || i}
                              className={`border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--surface)] cursor-pointer ${selectedTradeIds.includes(t.id) ? "bg-amber-400/5" : ""}`}
                              onClick={() => t.id && toggleTradeId(t.id)}
                              data-testid={`day-trade-row-${i}`}
                            >
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={selectedTradeIds.includes(t.id)}
                                  onChange={() => t.id && toggleTradeId(t.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`day-trade-checkbox-${i}`}
                                  className="accent-amber-400 cursor-pointer"
                                />
                              </td>
                              <td className="p-2 text-[color:var(--fg-2)]">{t.time}</td>
                              <td className="p-2 text-[color:var(--fg)] font-semibold">{t.symbol}</td>
                              <td className="p-2 text-right text-[color:var(--fg-2)]">{t.qty}</td>
                              <td className={`p-2 text-right ${t.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(t.pnl)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <button onClick={() => filterTradesToDay(selectedDay)} data-testid="filter-trades-day"
                  className="w-full bg-white text-black hover:bg-[color:var(--accent)] transition-colors py-3 text-sm flex items-center justify-center gap-2">
                  <Filter className="h-4 w-4" strokeWidth={1.5} /> FILTER TRADES TO THIS DAY
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
